import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiBadge, MiButton, ZoomInput } from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { mdiFilterVariant } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { CalendarFilterGroup } from "./CalendarFilterGroup";
import type {
  CalendarFilterGroupController,
  CalendarFilters,
} from "./useCalendarFilterGroup";

function countAppliedFilters(applied: CalendarFilters): number {
  return Object.values(applied).filter(Boolean).length;
}

export const CalendarToolBar = ({
  zoom,
  setZoom,
  showCalendarFilters,
  toggleCalendarFilters,
  filters,
  appliedFilters,
  builderOptions,
  communityOptions,
  statusOptions,
  hasPendingFilters,
  onApplyFilters,
  onResetFilters,
  handleFilterChange,
  title,
}: CalendarToolBarProps) => {
  const theme = useTheme();

  const appliedCount = useMemo(
    () => countAppliedFilters(appliedFilters),
    [appliedFilters]
  );

  const { classes, cx } = useStyles();

  return (
    <Box className={classes.container}>
      <Box className={classes.topRow}>
        <Box>
          <Typography variant="HD2">{title}</Typography>
        </Box>
        <Box className={classes.calendarControls}>
          <Box className={classes.zoomWrapper}>
            <ZoomInput
              value={zoom}
              handleChange={(e: SelectChangeEvent<number>) =>
                setZoom(e.target.value)
              }
              className={classes.zoomInput}
            />
          </Box>
          <Box className={classes.filterToggleRow}>
            <MiButton
              onClick={toggleCalendarFilters}
              variant={BUTTON_VARIANTS.OUTLINED}
              color={BUTTON_COLORS.BLACK}
              className={cx(
                classes.filterButton,
                appliedCount > 0 && classes.filterButtonActive
              )}
              startIcon={{ path: mdiFilterVariant }}
              title="Filter Calendar"
            >
              {appliedCount > 0 && (
                <MiBadge
                  text={String(appliedCount)}
                  fontSize={11}
                  backgroundColor={theme.palette.teal.main}
                  rounded
                  className={classes.badge}
                />
              )}
            </MiButton>
          </Box>
        </Box>
      </Box>
      {showCalendarFilters && (
        <Box className={classes.filterGroup}>
          <CalendarFilterGroup
            filters={filters}
            builderOptions={builderOptions}
            communityOptions={communityOptions}
            statusOptions={statusOptions}
            hasPendingFilters={hasPendingFilters}
            onApplyFilters={onApplyFilters}
            onResetFilters={onResetFilters}
            onToggleFilters={toggleCalendarFilters}
            handleFilterChange={handleFilterChange}
          />
        </Box>
      )}
    </Box>
  );
};

type CalendarToolBarProps = Pick<
  CalendarFilterGroupController,
  | "filters"
  | "appliedFilters"
  | "builderOptions"
  | "communityOptions"
  | "statusOptions"
  | "handleFilterChange"
> & {
  title?: string;
  zoom: number;
  setZoom: (zoom: number) => void;
  showCalendarFilters: boolean;
  toggleCalendarFilters: () => void;
  hasPendingFilters: boolean;
  onApplyFilters: () => void;
  onResetFilters: () => void;
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: theme.spacing(2),
    margin: theme.spacing(0, 1),
    padding: theme.spacing(0, 2, 1, 2),
    backgroundColor: theme.palette.background.default,
  },
  topRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: theme.spacing(2),
    width: "100%",
  },
  calendarControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  filterToggleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  filterButton: {
    height: 24,
    fontSize: 12,
    borderColor: theme.palette.grey.darkAccent,
    borderRadius: 2,
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  filterButtonActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light,
  },
  filterGroup: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  badge: {
    padding: theme.spacing(0, 1),
    height: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  zoomWrapper: {
    flexShrink: 0,
  },
  zoomInput: {
    "& .MuiSelect-select": {
      fontSize: theme.fontSize.small,
    },
  },
}));
